import cn from 'classnames';
import React, { Fragment } from 'react';
import { useDispatch } from 'react-redux';

import ForcedAutopayConsent from 'components/Core/Autopay-schedule/ForcedAutopay/ForcedAutopayConsent';
import PayButton from 'components/Core/Payment/Buttons/PayButton/PayButton';
import CreditCardSurchargingModal from 'components/Experiments/creditCardSurcharging/creditCardSurchargingModal';
import PaymentDisclaimer from 'components/Shared/PaymentDisclaimer/PaymentDisclaimer';
import { isSurchargePaintedDoorOnForSale } from 'components/Shared/creditCardSurcharging/creditCardSurcharging';
import SubscriptionConsent from 'components/Subscription/Consent/SubscriptionConsent';
import SegmentIO, { IxpExperiment } from 'reporting/SegmentIO.js';
import { SplunkReporter } from 'reporting/splunk/SplunkReporter';
import { isCOFEligiblePaymentMethod } from 'shared/PaymentHelpers';
import { isBatchPaymentsEnabled as selectIsBatchPaymentsEnabled } from 'store/ixp/selector';
import { modalActions } from 'store/modal/slice';
import { useSelector } from 'store/utils';
import { colors, breakpoints } from 'styles/cp';
import { Auth } from 'types/Auth';
import { Customizations } from 'types/CompanyInfo';
import { Payment } from 'types/Payment';
import { Wallet } from 'types/Wallet';
import { AUTOPAY_MAP } from 'types/constants';

const splunkReporter = SplunkReporter.getInstance();

const logger = 'components/Core/Payment/Buttons/PayButton/PayButtonWithConsent.tsx';

interface Props {
  currency: string;
  config: Record<string, any>;
  wallet: Pick<Wallet, 'cardOnFile' | 'fetchWalletStatus'>;
  auth: Pick<Auth, 'isUserSignedIn'>;
  ixp: { [key: string]: IxpExperiment };
  payment: Pick<
    Payment,
    | 'paymentError'
    | 'payPalProcessor'
    | 'paymentMethodType'
    | 'paymentStatus'
    | 'amount'
    | 'isAmountValid'
    | 'isDateScheduled'
    | 'isAutoPayOn'
    | 'isSubscriptionUpdatePaymentMethodRequired'
    | 'isPayPalCommerceInvoice'
  >;
  featureFlags: Record<string, any>;
  onPayButtonClicked: () => void;
  recurringInfo: Record<string, any>;
  dueDate: string;
  payMessageType?: any;
  isSubscription: boolean;
  customizations: Customizations;
  subscriptionInfo?: Record<string, any>;
  isPPCheckoutExperiment: boolean;
  className?: string;
  isForcedAutoPay?: boolean;
  isInEnhancementsHoldoutExperiment?: boolean;
}

const PayButtonWithConsent: React.FC<Props> = ({
  auth,
  config,
  ixp,
  currency,
  dueDate,
  featureFlags,
  onPayButtonClicked,
  payMessageType,
  payment,
  recurringInfo,
  wallet,
  isSubscription,
  subscriptionInfo,
  isPPCheckoutExperiment,
  className,
  customizations,
  isForcedAutoPay,
  isInEnhancementsHoldoutExperiment,
}) => {
  const dispatch = useDispatch();
  const { paymentMethodType } = payment;
  const storeFeatureFlags = useSelector((state) => state.featureFlags || {});
  const sale = useSelector((state) => state.sale);
  const insight = useSelector((state) => state.insight);
  const isCCSurchargePTD = isSurchargePaintedDoorOnForSale({
    featureFlags: storeFeatureFlags,
    sale,
  });

  // Get batch payment info for disclaimer
  const isBatchPaymentsEnabled = useSelector(selectIsBatchPaymentsEnabled);
  const selectedBatchInvoices = insight?.selectedBatchInvoices || [];
  const isBatchPayment = isBatchPaymentsEnabled && selectedBatchInvoices.length > 0;
  const selectedInvoicesCount = selectedBatchInvoices.length;

  const isCardOnFileEnabled =
    (wallet?.cardOnFile && isCOFEligiblePaymentMethod(paymentMethodType)) ?? false;

  const enhancedPayment = insight?.isPreview
    ? {
        ...payment,
        isAutoPayOn:
          sale.scheduleInfo?.autoPayType === AUTOPAY_MAP.TYPE.FORCED_CONSENT &&
          !!sale.recurringInfo,
      }
    : payment;

  const isApplePay = paymentMethodType === 'ap';
  const shouldShowMobileFixed = !wallet?.cardOnFile;
  const renderConsentComponent = () => {
    if (isSubscription) {
      return <SubscriptionConsent />;
    }

    if (isForcedAutoPay) {
      return <ForcedAutopayConsent />;
    }

    return (
      <PaymentDisclaimer
        payment={payment as any}
        currency={currency}
        dueDate={dueDate}
        isBatchPayment={isBatchPayment}
        selectedInvoicesCount={selectedInvoicesCount}
        isCardOnFileEnabled={isCardOnFileEnabled}
        isInEnhancementsHoldoutExperiment={isInEnhancementsHoldoutExperiment}
      />
    );
  };

  const openCCsurchargeModal = () => {
    dispatch(
      modalActions.show({
        background: 'inherit',
        component: CreditCardSurchargingModal,
        componentProps: {
          onContinue: () => window.location.reload(),
          hide: () => window.location.reload(),
        },
      })
    );
  };

  const onClickFunction = () => {
    if (isCCSurchargePTD) {
      // logs events
      splunkReporter.contextual({
        logInfo: { logLevel: 'info', logger },
        event: 'view',
        action: 'render',
        activityInfo: {
          activityObject: 'Surcharge PDT',
          event: 'pay_button_clicked',
          button: isApplePay ? 'apple_pay' : 'credit_card',
        },
      });

      if (isApplePay) {
        SegmentIO.transactionSubmitted();
        openCCsurchargeModal();
      } else {
        onPayButtonClicked();
      }
    } else {
      onPayButtonClicked();
    }
  };

  return (
    <Fragment>
      <div
        className={cn(className, 'w', {
          paypalcheckout: isPPCheckoutExperiment,
        })}
      >
        {renderConsentComponent()}
        <PayButton
          // @ts-ignore
          customizations={customizations}
          currency={currency}
          config={config}
          wallet={wallet}
          auth={auth}
          payment={enhancedPayment}
          ixp={ixp}
          featureFlags={featureFlags}
          onPayButtonClicked={onClickFunction}
          recurringInfo={recurringInfo}
          payMessageType={payMessageType}
          isSubscription={isSubscription}
          subscriptionInfo={subscriptionInfo}
          isForcedAutoPay={isForcedAutoPay}
        />
      </div>

      {/*language=SCSS*/}
      <style jsx>{`
        .w {
          :global(.main-footer) {
            display: none;
          }
          @media screen and (max-width: ${breakpoints.md}) {
            position: ${shouldShowMobileFixed && paymentMethodType ? 'fixed' : 'static'};
            padding: ${shouldShowMobileFixed ? '20px' : '0'};
            z-index: ${shouldShowMobileFixed ? '1098' : 'auto'};
            left: ${shouldShowMobileFixed ? '0' : 'auto'};
            right: ${shouldShowMobileFixed ? '0' : 'auto'};
            bottom: ${shouldShowMobileFixed ? '0' : 'auto'};
            background-color: ${shouldShowMobileFixed ? 'white' : 'transparent'};
          }
          .ApplePay {
            margin-bottom: 12px;
            .or-payment {
              display: flex;
              color: ${colors.gray02};
              justify-content: space-around;
              align-items: center;
              margin: 12px 0;
            }
          }
        }

        .paypalcheckout {
          margin-top: 20px;
          @media screen and (max-width: ${breakpoints.md}) {
            position: inherit;
            padding: 0px;
          }
        }
      `}</style>
    </Fragment>
  );
};

export default PayButtonWithConsent;
